/**
 * AdminSportsbook — parent admin page. Rendered inside the shared
 * `Admin` layout (which already wraps the outlet in AdminShell), so
 * this component only exports content, no shell.
 *
 * Tabs: Config · Transactions · Sessions
 * Tab state lives in URL search-params (`?tab=…`) so admins can share
 * links + browser back works naturally.
 */
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";
import { KeyRound, ListChecks, Trophy } from "lucide-react";

import SabaConfigForm from "../../components/sportsbook/SabaConfigForm";
import SabaTransactionsTable from "../../components/sportsbook/SabaTransactionsTable";
import SabaSessionsTable from "../../components/sportsbook/SabaSessionsTable";

const TABS = [
  { key: "config",       label: "Configuration",  icon: KeyRound },
  { key: "transactions", label: "Transactions",   icon: ListChecks },
  { key: "sessions",     label: "Sessions",       icon: Trophy },
];

export default function AdminSportsbook() {
  const [params, setParams] = useSearchParams();
  const tab = useMemo(() => {
    const t = params.get("tab");
    return TABS.some((x) => x.key === t) ? t : "config";
  }, [params]);

  const setTab = (t) => setParams({ tab: t });

  return (
    <div className="space-y-6" data-testid="admin-sportsbook">
      <Toaster richColors position="top-right" />
      <div className="flex items-center gap-3">
        <Trophy className="w-5 h-5 text-gold" />
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold">Sportsbook</div>
          <h1 className="font-display italic text-2xl text-ink leading-tight">
            SABA Sports operations
          </h1>
        </div>
      </div>

      {/* Tab strip — reuses the visual language of the admin sidebar's
          active state (gold underline on the current tab). */}
      <div className="flex items-center gap-1 border-b border-line" data-testid="admin-sportsbook-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={
                "flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2 text-sm transition " +
                (active
                  ? "border-gold text-gold"
                  : "border-transparent text-ink-3 hover:text-ink")
              }
              data-testid={`admin-sportsbook-tab-${t.key}`}
              aria-current={active ? "page" : undefined}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "config" && <SabaConfigForm />}
      {tab === "transactions" && <SabaTransactionsTable />}
      {tab === "sessions" && <SabaSessionsTable />}
    </div>
  );
}
